import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { categories } from "@/mocks/categories";
import { getAllArticles } from "@/mocks/articles";

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [articlesOpen, setArticlesOpen] = useState(false);
  const [categoriesOpen, setCategoriesOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const articlesRef = useRef<HTMLDivElement>(null);
  const categoriesRef = useRef<HTMLDivElement>(null);
  const articles = getAllArticles();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (articlesRef.current && !articlesRef.current.contains(e.target as Node)) {
        setArticlesOpen(false);
      }
      if (categoriesRef.current && !categoriesRef.current.contains(e.target as Node)) {
        setCategoriesOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const closeAll = () => {
    setMobileOpen(false);
    setArticlesOpen(false);
    setCategoriesOpen(false);
  };

  return (
    <nav
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        scrolled
          ? "bg-background-50/95 backdrop-blur-md border-b border-background-200/60"
          : "bg-background-50/40 backdrop-blur-sm"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6 flex items-center justify-between h-16">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 flex-shrink-0" onClick={closeAll}>
          <span className="w-8 h-8 rounded-lg bg-primary-500 flex items-center justify-center">
            <i className="ri-database-2-line text-white text-sm"></i>
          </span>
          <span className="font-heading text-lg font-bold text-foreground-950 tracking-tight whitespace-nowrap drop-shadow-sm">
            pandabuy<span className="text-primary-600">.watch</span>
          </span>
        </Link>

        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center gap-2.5">
          <Link
            to="/"
            onClick={closeAll}
            className="px-3 py-2 rounded-full text-foreground-700 text-sm font-medium hover:bg-background-200/70 transition-colors cursor-pointer whitespace-nowrap"
          >
            Home
          </Link>

          {/* Articles dropdown */}
          <div className="relative" ref={articlesRef}>
            <button
              onClick={() => setArticlesOpen(!articlesOpen)}
              className="flex items-center gap-1.5 px-3 py-2 rounded-full text-foreground-700 text-sm font-medium hover:bg-background-200/70 transition-colors cursor-pointer whitespace-nowrap"
            >
              <span>Articles</span>
              <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${articlesOpen ? "rotate-180" : ""}`}></i>
            </button>
            {articlesOpen && (
              <div className="absolute top-full mt-2 right-0 w-80 bg-background-50 rounded-xl border border-background-200/70 shadow-lg py-2 max-h-[420px] overflow-y-auto z-50">
                {articles.map((art) => (
                  <Link
                    key={art.slug}
                    to={`/article/${art.slug}`}
                    onClick={closeAll}
                    className="flex items-start gap-3 px-4 py-2.5 hover:bg-background-100 transition-colors cursor-pointer group"
                  >
                    <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0 bg-background-200">
                      <img src={art.image} alt={art.title} className="w-full h-full object-cover" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className="text-xs text-primary-600 font-semibold">{art.tag}</span>
                      <p className="text-xs text-foreground-800 group-hover:text-foreground-950 leading-snug line-clamp-2 mt-0.5">{art.title}</p>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* Categories dropdown */}
          <div className="relative" ref={categoriesRef}>
            <button
              onClick={() => { setArticlesOpen(false); setCategoriesOpen(!categoriesOpen); }}
              className="flex items-center gap-2 px-3 py-2 rounded-full bg-background-100 border border-background-200/70 text-foreground-700 text-sm font-medium hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-grid-line text-sm"></i>
              <span>Categories</span>
              <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${categoriesOpen ? "rotate-180" : ""}`}></i>
            </button>
            {categoriesOpen && (
              <div className="absolute top-full mt-2 left-0 w-72 bg-background-50 rounded-xl border border-background-200/70 shadow-lg py-2 max-h-[420px] overflow-y-auto z-50">
                {categories.map((cat, i) => {
                  const dotColors = [
                    "bg-primary-500", "bg-accent-500", "bg-secondary-500",
                    "bg-primary-400", "bg-accent-400", "bg-secondary-400",
                    "bg-primary-500", "bg-accent-500", "bg-secondary-500",
                    "bg-primary-400", "bg-accent-400", "bg-secondary-400",
                    "bg-primary-500", "bg-accent-500",
                  ];
                  return (
                    <a
                      key={i}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      onClick={closeAll}
                      className="flex items-center gap-3 px-4 py-2.5 hover:bg-background-100 transition-colors cursor-pointer group"
                    >
                      <span className={`w-2 h-2 rounded-full flex-shrink-0 ${dotColors[i % dotColors.length]}`}></span>
                      <span className="text-sm text-foreground-800 group-hover:text-foreground-950 flex-1 whitespace-nowrap">{cat.name}</span>
                      <span className="text-xs text-foreground-400 whitespace-nowrap">{cat.count}</span>
                    </a>
                  );
                })}
              </div>
            )}
          </div>

          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="px-3 py-2 rounded-full border border-foreground-300 text-foreground-700 text-sm font-medium hover:bg-foreground-100/50 transition-colors cursor-pointer whitespace-nowrap"
          >
            Pandabuy Sheet
          </a>
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="px-4 py-2 rounded-full bg-foreground-950 text-background-50 text-sm font-semibold hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap"
          >
            Buy It Now
          </a>
        </div>

        {/* Mobile hamburger */}
        <div className="flex md:hidden items-center gap-2">
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="w-9 h-9 flex items-center justify-center rounded-lg border border-background-200/70 bg-background-50/80 text-foreground-700 cursor-pointer"
          >
            <i className={`text-lg ${mobileOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
          </button>
        </div>
      </div>

      {/* Mobile menu panel */}
      {mobileOpen && (
        <div className="md:hidden bg-background-50/98 backdrop-blur-md border-t border-background-200/60 px-4 py-4 max-h-[70vh] overflow-y-auto">
          <div className="flex flex-col gap-1 mb-3">
            <Link
              to="/"
              onClick={closeAll}
              className="flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-background-100 transition-colors text-sm font-medium text-foreground-800"
            >
              <i className="ri-home-line text-primary-500"></i>
              Home
            </Link>
            <div className="px-3 py-2 text-xs font-semibold text-foreground-500 uppercase tracking-wide">Articles</div>
            {articles.slice(0, 5).map((art) => (
              <Link
                key={art.slug}
                to={`/article/${art.slug}`}
                onClick={closeAll}
                className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-background-100 transition-colors"
              >
                <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0 bg-background-200">
                  <img src={art.image} alt={art.title} className="w-full h-full object-cover" />
                </div>
                <span className="text-xs text-foreground-800 line-clamp-2 flex-1">{art.title}</span>
              </Link>
            ))}
            <Link
              to="/article/pandabuy-spreadsheet-guide"
              onClick={closeAll}
              className="px-3 py-2 text-xs text-primary-600 font-medium hover:underline"
            >
              View all 10 articles →
            </Link>
          </div>

          <div className="border-t border-background-200/60 pt-3">
            <div className="px-3 py-2 text-xs font-semibold text-foreground-500 uppercase tracking-wide">Categories</div>
            <div className="grid grid-cols-2 gap-1">
              {categories.map((cat, i) => (
                <a
                  key={i}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={closeAll}
                  className="flex items-center gap-2 px-2 py-2 rounded-lg hover:bg-background-100 transition-colors text-xs text-foreground-800"
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-primary-500 flex-shrink-0"></span>
                  <span className="line-clamp-1">{cat.name}</span>
                </a>
              ))}
            </div>
          </div>

          <div className="flex gap-2 pt-3 mt-3 border-t border-background-200/60">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="flex-1 px-3 py-2.5 rounded-full border border-foreground-300 text-foreground-700 text-xs font-medium text-center whitespace-nowrap"
            >
              Pandabuy Sheet
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="flex-1 px-3 py-2.5 rounded-full bg-foreground-950 text-background-50 text-xs font-semibold text-center whitespace-nowrap"
            >
              Buy It Now
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}